// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Work Order Service
 *
 * Typed wrappers over the BFF's work-order proxy, which fronts the
 * helios-work-order work-order-api — itself a proxy over an external
 * work-order management system. Helios stores no work orders.
 *
 * Work orders belong to a site: `siteId` selects
 * `/api/v1/sites/{site_id}/work-orders`. A null site is the whole fleet, served
 * only by the deprecated unscoped `/api/v1/work-orders` routes, which upstream
 * keeps until every caller names a site.
 *
 * The WOMS exposes no concurrency token — no ETag, no version field — so the
 * writes here are last-write-wins. A change made in the provider's own system
 * between a read and a write is overwritten silently, and no guard can be added
 * at this layer to prevent it.
 */

import { ENDPOINTS } from '../utils/constants';
import { get, patch, post, type ApiError } from './api';
import type {
  BusinessOwnerList,
  ProviderList,
  WorkOrder,
  WorkOrderCreateRequest,
  WorkOrderListParams,
  WorkOrderPatchRequest,
  WorkOrderSummary,
  WorkOrdersPage,
} from '../types';

/** The site a call is scoped to; null is the whole fleet (deprecated routes). */
export type WorkOrderSite = string | null;

/**
 * `status` repeats rather than joining, matching the BFF's `list[str]` query
 * param — a comma-joined value arrives as one unparsed status and filters
 * nothing.
 */
function buildQuery(params: WorkOrderListParams): string {
  const search = new URLSearchParams();
  if (params.sort) search.set('sort', params.sort);
  params.status?.forEach(status => search.append('status', status));
  if (params.title) search.set('title', params.title);
  if (params.provider) search.set('provider', params.provider);
  if (params.updated_since) search.set('updated_since', params.updated_since);
  if (params.page_size !== undefined) search.set('page_size', String(params.page_size));
  if (params.cursor) search.set('cursor', params.cursor);
  const query = search.toString();
  return query ? `?${query}` : '';
}

export function listWorkOrders(
  siteId: WorkOrderSite,
  params: WorkOrderListParams = {}
): Promise<WorkOrdersPage> {
  return get<WorkOrdersPage>(`${ENDPOINTS.WORK_ORDERS(siteId)}${buildQuery(params)}`);
}

/**
 * One work order, or null when the id is unknown. A 404 is a normal outcome
 * for a stale link, an order removed upstream, or one that belongs to another
 * site, so it is mapped rather than thrown — every other status still raises.
 */
export async function getWorkOrder(
  siteId: WorkOrderSite,
  workOrderId: string
): Promise<WorkOrder | null> {
  try {
    return await get<WorkOrder>(ENDPOINTS.WORK_ORDER_BY_ID(siteId, workOrderId));
  } catch (err) {
    if ((err as ApiError).status === 404) return null;
    throw err;
  }
}

export function getWorkOrderSummary(siteId: WorkOrderSite): Promise<WorkOrderSummary> {
  return get<WorkOrderSummary>(ENDPOINTS.WORK_ORDERS_SUMMARY(siteId));
}

/**
 * Route approved work to a provider. Only `title` is required upstream.
 *
 * `provider` must be a name from {@link listProviders} and `business_owner`
 * must identify exactly one entry of {@link listBusinessOwners} — an unknown
 * value comes back as a 400, not a silently unassigned order, so the caller
 * should surface the message rather than swallow it.
 */
export function createWorkOrder(
  siteId: WorkOrderSite,
  request: WorkOrderCreateRequest
): Promise<WorkOrder> {
  return post<WorkOrder>(ENDPOINTS.WORK_ORDERS(siteId), request);
}

/**
 * Partial update, including status. Last-write-wins: there is no expected-state
 * guard because the WOMS offers nothing to build one from. The response is the
 * authoritative record as stored upstream, which is why callers should seed
 * their cache from it rather than from what they sent.
 */
export function patchWorkOrder(
  siteId: WorkOrderSite,
  workOrderId: string,
  request: WorkOrderPatchRequest
): Promise<WorkOrder> {
  return patch<WorkOrder>(ENDPOINTS.WORK_ORDER_BY_ID(siteId, workOrderId), request);
}

/** The upstream users a work order can be assigned to; `business_owner` reads back as their id. */
export function listBusinessOwners(): Promise<BusinessOwnerList> {
  return get<BusinessOwnerList>(ENDPOINTS.WORK_ORDER_BUSINESS_OWNERS);
}

/** The upstream vendors work can be routed to; send a `name` as `provider`. */
export function listProviders(): Promise<ProviderList> {
  return get<ProviderList>(ENDPOINTS.WORK_ORDER_PROVIDERS);
}
